import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { fmtDateTime, apiErr } from "@/lib/format";
import { toast } from "sonner";
import {
  ClockCounterClockwiseIcon, FilePdfIcon, DownloadSimpleIcon, EnvelopeSimpleIcon,
  MagnifyingGlassIcon,
} from "@phosphor-icons/react";

export default function Audit() {
  const [tab, setTab] = useState("system");
  return (
    <Layout title="Auditoría" subtitle="Bitácora completa de operaciones del sistema">
      <div className="flex gap-1 mb-4 border-b border-border/60" data-testid="audit-tabs">
        <TabBtn active={tab === "system"} onClick={() => setTab("system")} icon={ClockCounterClockwiseIcon} testid="tab-system">
          Bitácora del sistema
        </TabBtn>
        <TabBtn active={tab === "pdf"} onClick={() => setTab("pdf")} icon={FilePdfIcon} testid="tab-pdf">
          Trazabilidad de PDF
        </TabBtn>
      </div>

      {tab === "system" && <SystemAuditTab />}
      {tab === "pdf" && <PdfAccessTab />}
    </Layout>
  );
}

function TabBtn({ active, onClick, icon: Icon, testid, children }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`px-4 py-2 text-sm flex items-center gap-2 border-b-2 -mb-px transition ${active ? "border-agro-green text-agro-green" : "border-transparent text-text-muted hover:text-text-primary"}`}>
      <Icon size={14} /> {children}
    </button>
  );
}

function SystemAuditTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(false);
  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/audit", { params: { limit: 200 } }); setItems(data); }
      catch (e) { if (e?.response?.status === 403) setDenied(true); else toast.error(apiErr(e)); }
      finally { setLoading(false); }
    })();
  }, []);

  if (denied) return (
    <div className="bg-card border border-red-500/30 rounded-lg p-8 text-center text-text-secondary">
      <div className="text-agro-yellow font-heading text-xl mb-2">Acceso restringido</div>
      Solo el administrador puede consultar la bitácora.
    </div>
  );

  return (
    <div className="bg-card border border-border/60 rounded-lg overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-sidebar">
            <tr className="text-left text-[10px] uppercase tracking-wider text-text-muted">
              <th className="p-3 pl-5">Fecha/Hora</th><th className="p-3">Usuario</th>
              <th className="p-3">Acción</th><th className="p-3">Entidad</th>
              <th className="p-3">ID</th><th className="p-3 pr-5">IP</th>
            </tr>
          </thead>
          <tbody>
            {loading ? <tr><td colSpan="6" className="p-8 text-center text-text-muted">Cargando…</td></tr>
            : items.length === 0 ? <tr><td colSpan="6" className="p-12 text-center text-text-muted">Sin registros</td></tr>
            : items.map((a) => (
              <tr key={a.id} className="border-t border-border/40 hover:bg-white/[0.02]" data-testid={`audit-row-${a.id}`}>
                <td className="p-3 pl-5 text-xs font-mono text-text-secondary">{fmtDateTime(a.timestamp)}</td>
                <td className="p-3 text-sm">{a.user_email || "—"}</td>
                <td className="p-3"><span className="text-[10px] uppercase px-2 py-0.5 rounded bg-agro-green/10 text-agro-green border border-agro-green/30">{a.action}</span></td>
                <td className="p-3 text-sm">{a.entity}</td>
                <td className="p-3 font-mono text-[10px] text-text-muted truncate max-w-[180px]">{a.entity_id || "—"}</td>
                <td className="p-3 pr-5 text-xs text-text-secondary">{a.ip || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PdfAccessTab() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(false);
  const [filters, setFilters] = useState({
    resource_type: "",
    action: "",
    user_email: "",
    desde: "",
    hasta: "",
  });

  const load = async () => {
    setLoading(true);
    try {
      const params = { limit: 500 };
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const { data } = await api.get("/pdf-access-logs", { params });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      if (e?.response?.status === 403) setDenied(true);
      else toast.error(apiErr(e));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const upd = (k, v) => setFilters({ ...filters, [k]: v });
  const clear = () => setFilters({ resource_type: "", action: "", user_email: "", desde: "", hasta: "" });

  if (denied) return (
    <div className="bg-card border border-red-500/30 rounded-lg p-8 text-center text-text-secondary">
      <div className="text-agro-yellow font-heading text-xl mb-2">Acceso restringido</div>
      Solo el administrador o contador puede consultar la trazabilidad de PDF.
    </div>
  );

  const totalDL = items.filter((i) => i.action === "download").length;
  const totalEmail = items.filter((i) => i.action === "email").length;

  return (
    <div className="space-y-4">
      {/* Resumen */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Total registros" value={total} testid="pdf-stat-total" />
        <StatCard label="Descargas" value={totalDL} accent="text-agro-green" testid="pdf-stat-downloads" />
        <StatCard label="Envíos por correo" value={totalEmail} accent="text-agro-yellow" testid="pdf-stat-emails" />
        <StatCard label="Usuarios únicos" value={new Set(items.map((i) => i.user_email)).size} testid="pdf-stat-users" />
      </div>

      {/* Filtros */}
      <div className="bg-card border border-border/60 rounded-lg p-4" data-testid="pdf-filters">
        <div className="grid grid-cols-1 md:grid-cols-6 gap-2">
          <Sel v={filters.resource_type} on={(v) => upd("resource_type", v)} testid="filter-resource-type" opts={[
            ["", "Todos los documentos"],
            ["invoice", "Facturas"],
            ["proforma", "Proformas"],
            ["purchase_order", "Órdenes de compra"],
            ["receipt", "Recibos de pago"],
            ["shipping_guide", "Guías de envío"],
          ]} />
          <Sel v={filters.action} on={(v) => upd("action", v)} testid="filter-action" opts={[
            ["", "Toda acción"],
            ["download", "Descarga"],
            ["email", "Envío por correo"],
            ["print", "Impresión"],
          ]} />
          <input value={filters.user_email} onChange={(e) => upd("user_email", e.target.value)}
            placeholder="Filtrar por correo…"
            className="bg-app border border-border rounded-md px-3 py-1.5 text-sm"
            data-testid="filter-user-email" />
          <input type="date" value={filters.desde} onChange={(e) => upd("desde", e.target.value)}
            className="bg-app border border-border rounded-md px-3 py-1.5 text-sm"
            data-testid="filter-desde" />
          <input type="date" value={filters.hasta} onChange={(e) => upd("hasta", e.target.value)}
            className="bg-app border border-border rounded-md px-3 py-1.5 text-sm"
            data-testid="filter-hasta" />
          <div className="flex gap-1.5">
            <button onClick={load} className="flex-1 bg-agro-green text-black text-sm font-medium rounded-md px-3 py-1.5 flex items-center justify-center gap-1"
              data-testid="pdf-filter-apply">
              <MagnifyingGlassIcon size={12} /> Buscar
            </button>
            <button onClick={() => { clear(); setTimeout(load, 100); }} className="border border-border text-xs rounded-md px-3"
              data-testid="pdf-filter-clear">Limpiar</button>
          </div>
        </div>
      </div>

      {/* Tabla */}
      <div className="bg-card border border-border/60 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-sidebar">
              <tr className="text-left text-[10px] uppercase tracking-wider text-text-muted">
                <th className="p-3 pl-5">Fecha/Hora</th>
                <th className="p-3">Usuario</th>
                <th className="p-3">Acción</th>
                <th className="p-3">Documento</th>
                <th className="p-3">N°</th>
                <th className="p-3">Destinatarios</th>
                <th className="p-3 pr-5">IP · Navegador</th>
              </tr>
            </thead>
            <tbody data-testid="pdf-logs-tbody">
              {loading ? <tr><td colSpan="7" className="p-8 text-center text-text-muted">Cargando…</td></tr>
              : items.length === 0 ? <tr><td colSpan="7" className="p-12 text-center text-text-muted">Sin registros que coincidan</td></tr>
              : items.map((r) => (
                <tr key={r.id} className="border-t border-border/40 hover:bg-white/[0.02]" data-testid={`pdf-log-row-${r.id}`}>
                  <td className="p-3 pl-5 text-xs font-mono text-text-secondary whitespace-nowrap">{fmtDateTime(r.timestamp)}</td>
                  <td className="p-3 text-sm">
                    <div>{r.user_name || r.user_email}</div>
                    <div className="text-[10px] text-text-muted">{r.user_email} · {r.user_role}</div>
                  </td>
                  <td className="p-3">
                    {r.action === "download" ? (
                      <span className="text-[10px] uppercase px-2 py-0.5 rounded bg-agro-green/10 text-agro-green border border-agro-green/30 inline-flex items-center gap-1">
                        <DownloadSimpleIcon size={10} weight="bold" /> Descarga
                      </span>
                    ) : r.action === "email" ? (
                      <span className="text-[10px] uppercase px-2 py-0.5 rounded bg-agro-yellow/10 text-agro-yellow border border-agro-yellow/30 inline-flex items-center gap-1">
                        <EnvelopeSimpleIcon size={10} weight="bold" /> Correo
                      </span>
                    ) : (
                      <span className="text-[10px] uppercase px-2 py-0.5 rounded bg-white/5 text-text-secondary border border-border">{r.action}</span>
                    )}
                  </td>
                  <td className="p-3 text-xs">{labelFor(r.resource_type)}</td>
                  <td className="p-3 text-xs font-mono">{r.resource_number || r.resource_id?.slice(0, 8)}</td>
                  <td className="p-3 text-xs text-text-secondary max-w-[240px]">
                    {(r.recipients || []).length > 0
                      ? (r.recipients || []).join(", ")
                      : <span className="text-text-muted">—</span>}
                  </td>
                  <td className="p-3 pr-5 text-[10px] text-text-muted">
                    <div>{r.ip}</div>
                    <div className="truncate max-w-[220px]" title={r.user_agent}>{shortUA(r.user_agent)}</div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, accent = "text-text-primary", testid }) {
  return (
    <div className="bg-card border border-border/60 rounded-lg p-4" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider text-text-muted">{label}</div>
      <div className={`font-heading text-2xl mt-1 ${accent}`}>{value}</div>
    </div>
  );
}

function Sel({ v, on, opts, testid }) {
  return (
    <select value={v} onChange={(e) => on(e.target.value)}
      className="bg-app border border-border rounded-md px-2 py-1.5 text-sm"
      data-testid={testid}>
      {opts.map(([val, l]) => <option key={val} value={val}>{l}</option>)}
    </select>
  );
}

function labelFor(t) {
  return ({
    invoice: "Factura",
    proforma: "Proforma",
    purchase_order: "Orden de compra",
    receipt: "Recibo de pago",
    shipping_guide: "Guía de remisión",
  })[t] || t;
}

function shortUA(ua) {
  if (!ua) return "";
  if (ua.includes("Chrome")) return "Chrome";
  if (ua.includes("Firefox")) return "Firefox";
  if (ua.includes("Safari") && !ua.includes("Chrome")) return "Safari";
  if (ua.includes("Edge")) return "Edge";
  if (ua.includes("curl")) return "API/curl";
  return ua.slice(0, 30);
}
